import { useState, useEffect } from 'react';
import thTH from './th-TH.json';
import enUS from './en-US.json';
import esMX from './es-MX.json';

// Translation data
const translations: Record<string, any> = {
  'th-TH': thTH,
  'en-US': enUS,
  'es-MX': esMX
};

// Global state for language
let globalLocale = localStorage.getItem('locale') || 'th-TH';
const listeners: Set<() => void> = new Set();

// Notify all listeners when language changes
function notifyListeners() {
  listeners.forEach(listener => listener());
}

// Get nested value from object using dot notation
function getNestedValue(obj: any, path: string): string {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : path;
  }, obj);
}

// Translation hook
export function useTranslation() {
  const [currentLocale, setCurrentLocale] = useState<string>(globalLocale);

  useEffect(() => {
    const listener = () => {
      setCurrentLocale(globalLocale);
    };
    listeners.add(listener);
    
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const t = (key: string, params?: Record<string, any>): string => {
    const translation = getNestedValue(translations[currentLocale], key);
    
    if (typeof translation !== 'string') {
      return key;
    }

    // Replace parameters if provided
    if (params) {
      return Object.keys(params).reduce((result, paramKey) => {
        return result.replace(new RegExp(`{${paramKey}}`, 'g'), params[paramKey]);
      }, translation);
    }

    return translation;
  };

  const changeLanguage = (locale: string) => {
    globalLocale = locale;
    localStorage.setItem('locale', locale);
    notifyListeners();
  };

  return {
    t,
    currentLocale,
    changeLanguage,
    language: currentLocale === 'th-TH' ? 'th' : currentLocale === 'en-US' ? 'en' : 'th',
    setLanguage: (lang: string) => {
      const locale = lang === 'th' ? 'th-TH' : lang === 'en' ? 'en-US' : 'th-TH';
      changeLanguage(locale);
    }
  };
}

// Default export for compatibility
export default {
  useTranslation
};
